import { ContentReportDialog } from "Components/Support/ContentReport/ContentReportDialog";
import type { ContentReportTarget } from "Components/Support/ContentReport/ContentReportTarget";
import { useDisclosedNameResolver } from "Hooks/Chat/useDisclosedNameResolver";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import type { LocalMessage } from "stream-chat";
import { useChannelStateContext } from "stream-chat-react";

type ReportMessage = (message: LocalMessage) => void;

const ChatMessageReportContext = createContext<ReportMessage | null>(null);

type Props = {
  children: ReactNode;
};

/**
 * Owns the DSA notice dialog for the open channel (ZS-1743). Messages are rendered deep inside
 * stream-chat-react's list, so the per-message "Report" action hands the message up here through
 * context and one dialog instance serves the whole conversation. Mounted inside `<Channel>` —
 * it needs the channel for the target and the disclosed-name resolver for the author's name.
 */
export const ChatMessageReportProvider = ({ children }: Props) => {
  const { channel } = useChannelStateContext();
  const resolveName = useDisclosedNameResolver();
  const [target, setTarget] = useState<ContentReportTarget | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  // Remounts the dialog per report so each one starts from a blank form (see SupportButton).
  const [instanceKey, setInstanceKey] = useState(0);

  // Stable: every message in the list memoises its context on this function, so a new identity
  // per render would rebuild all of them on each provider render.
  const channelId = channel.id;
  const reportMessage: ReportMessage = useCallback(
    (message) => {
      if (!channelId) {
        return;
      }

      setTarget({
        kind: "message",
        channelId,
        messageId: message.id,
        authorHandle: null,
        // The resolver upgrades Stream's disclosable name to the real one where this viewer may
        // see it (ADR-0007), so the form names the author exactly as the bubble does.
        authorName: resolveName(message.user?.id, message.user?.name),
      });
      setInstanceKey((key) => key + 1);
      setIsOpen(true);
    },
    [channelId, resolveName],
  );

  return (
    <ChatMessageReportContext.Provider value={reportMessage}>
      {children}
      {target && (
        <ContentReportDialog
          key={instanceKey}
          isOpen={isOpen}
          target={target}
          onClose={() => setIsOpen(false)}
        />
      )}
    </ChatMessageReportContext.Provider>
  );
};

/** Null outside the provider, so a message rendered elsewhere (a thread preview, a search hit)
    simply offers no Report action. */
export const useChatMessageReport = () => useContext(ChatMessageReportContext);
